import type { ProjectArtist } from '@shared/schema';

/**
 * The project manager is the only permitted fallback for the project-header
 * shortcut. Keep this keyed by the canonical short code rather than a name or
 * a user id: ids are environment-specific and names are not identities.
 *
 * This mirrors PROJECT_MANAGER_SHORT_CODE in server/services/share-call.service.ts.
 */
export const PROJECT_MANAGER_SHORT_CODE = 'BEN';

export interface ProjectCallMember {
  id: number;
  isActive?: boolean;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
  shortCode?: string | null;
  colorCode?: string | null;
}

export interface ProjectCallRosterProject {
  leadArtist?: string | null;
  artists?: readonly Pick<ProjectArtist, 'artistCode'>[];
}

export type ProjectCallRecipientResolution =
  | {
      kind: 'ready';
      recipients: ProjectCallMember[];
      recipientNames: string[];
    }
  | {
      kind: 'unavailable';
      recipients: [];
      recipientNames: [];
      reason: 'no-assigned-user' | 'no-project-manager';
    };

function displayName(member: ProjectCallMember): string {
  const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ').trim();
  return fullName || member.shortCode || `User ${member.id}`;
}

/**
 * Resolve the recipients for the expanded project-chat video shortcut.
 *
 * `teamMembers` is the authenticated, active team-members query. Assignment
 * matching intentionally uses only the stable short code stored on the
 * project, never names or copied PII. A missing member therefore means that
 * account is not currently active/available in the roster and is not silently
 * replaced by another admin.
 */
export function resolveProjectChatVideoRecipients(
  project: ProjectCallRosterProject | null | undefined,
  teamMembers: readonly ProjectCallMember[],
  currentUserId: number,
): ProjectCallRecipientResolution {
  if (!project) {
    return {
      kind: 'unavailable',
      recipients: [],
      recipientNames: [],
      reason: 'no-assigned-user',
    };
  }

  const memberByShortCode = new Map<string, ProjectCallMember>();
  for (const member of teamMembers) {
    if (member.isActive === false) continue;
    if (member.shortCode) memberByShortCode.set(member.shortCode, member);
  }

  const recipientIds = new Set<number>();
  const recipients: ProjectCallMember[] = [];
  const assignedShortCodes = [
    project.leadArtist,
    ...(project.artists ?? []).map((artist) => artist.artistCode),
  ];

  for (const shortCode of assignedShortCodes) {
    if (!shortCode) continue;
    const member = memberByShortCode.get(shortCode);
    if (!member || member.id === currentUserId || recipientIds.has(member.id)) continue;
    recipientIds.add(member.id);
    recipients.push(member);
  }

  // A project with no other active assignee may ring BEN, but never another
  // admin and never the caller. The roster query itself is active-only.
  if (recipients.length === 0) {
    const projectManager = teamMembers.find(
      (member) =>
        member.isActive !== false &&
        member.shortCode === PROJECT_MANAGER_SHORT_CODE &&
        member.role === 'admin' &&
        member.id !== currentUserId,
    );
    if (projectManager) {
      recipients.push(projectManager);
    }
  }

  if (recipients.length === 0) {
    return {
      kind: 'unavailable',
      recipients: [],
      recipientNames: [],
      reason: teamMembers.some(
        (member) =>
          member.isActive !== false &&
          member.shortCode === PROJECT_MANAGER_SHORT_CODE &&
          member.role === 'admin' &&
          member.id === currentUserId,
      )
        ? 'no-project-manager'
        : 'no-assigned-user',
    };
  }

  return {
    kind: 'ready',
    recipients,
    recipientNames: recipients.map(displayName),
  };
}
